import React, { useEffect, useMemo, useState } from "react";
import { approverDisplayName } from "./workflowTestRun";
import { Alert, Button } from "@mui/material";
import WorkflowApprovalStage, { WorkflowApprovalStamp } from "./WorkflowApprovalStage";
import { PifApplicantSummary, pifRateBandFlag } from "./pifDetailsModel";
import { getWorkflowPifRateBand } from "../../services/workflowService";
import { toStampTime } from "./workflowRunState";
import useWorkflowApprovalStage, {
  WorkflowApprovalDecision,
  WorkflowApprovalOutcome,
  WorkflowApprovalTarget,
} from "../../hooks/useWorkflowApprovalStage";
import { useToaster } from "../../hooks/useToaster";
import { useUser } from "../../context/UserContext";
import { IMPERSONATION_INFO, LOCALSTORAGENAME, SEVERITIES } from "../../data/constants";
import { WorkflowApprovalStageGroupDto } from "../../types/workflowDto";

export interface WorkflowApprovalDecided {
  decision: WorkflowApprovalDecision;
  reason: string;
  /** Whether the counting model is now satisfied (the case moved on) or still waits. */
  stageResolved: boolean;
  /** The recorded stamp, for the terminal screen when this decision ended the case. */
  stamp: WorkflowApprovalStamp | null;
}

interface WorkflowApprovalLiveProps {
  target: WorkflowApprovalTarget;
  reference: string;
  definitionName: string;
  stageName: string;
  applicant: PifApplicantSummary | null;
  /** Reloads the case after a recorded decision; resolves false if that reload failed. */
  onDecided: (decided: WorkflowApprovalDecided) => Promise<boolean>;
  /** Reloads the case (after a conflict); resolves false if that failed. */
  onReloadCase: () => Promise<boolean>;
}

type ApprovalBanner = Exclude<WorkflowApprovalOutcome["kind"], "decided"> | "reload-failed";

/** Decided rows as attest stamps — name and time (no IP in v1), oldest first by DecidedOn. */
const toStamps = (group: WorkflowApprovalStageGroupDto | null): WorkflowApprovalStamp[] =>
  (group?.approvals ?? [])
    .filter((row) => row.decision !== "pending")
    .sort((a, b) => (a.decidedOn ?? "").localeCompare(b.decidedOn ?? ""))
    .map((row) => ({
      name: row.approverName || "Approver",
      decision: row.decision as WorkflowApprovalStamp["decision"],
      at: toStampTime(row.decidedOn),
    }));

/**
 * Who the screen is "viewing as" in the not-yours note. The stored role names are written at sign-in
 * and impersonation does not replace them, so while impersonating they are still the admin's own:
 * the person being viewed as is named instead (proposal P7).
 */
const viewingAsLabel = (firstName: string | null, lastName: string | null): string => {
  const name = [firstName, lastName].filter(Boolean).join(" ");
  if (localStorage.getItem(IMPERSONATION_INFO.IsImpersonating) === "true") return name || "this user";
  const role = (localStorage.getItem(LOCALSTORAGENAME.ROLENAMES) ?? "").split(",")[0]?.trim();
  return role || name || "this user";
};

const TOASTS: Record<WorkflowApprovalDecision, string> = {
  accept: "Accepted — signature stamped",
  reject: "Rejected — creator notified",
  cancel: "Case cancelled",
};

/** An approval stage on a real case: GET approvals for the approvers and stamps, approval/decide
 *  for the decision, and a banner for every way the decision can be refused. */
const WorkflowApprovalLive: React.FC<WorkflowApprovalLiveProps> = ({
  target,
  reference,
  definitionName,
  stageName,
  applicant,
  onDecided,
  onReloadCase,
}) => {
  const showToaster = useToaster();
  const { userId, firstName, lastName } = useUser();
  const { group, loadStatus, reload, deciding, decide } = useWorkflowApprovalStage(target);
  const [banner, setBanner] = useState<ApprovalBanner | null>(null);

  // pif-3 tells the supervisor "Directors will see a flag" (Epic 80430). Only the PIF has an
  // applicant summary, so only a PIF asks; the server works the flag out from the saved case.
  const isPif = applicant !== null;
  const [rateFlag, setRateFlag] = useState<ReturnType<typeof pifRateBandFlag>>(null);
  useEffect(() => {
    if (!isPif) return undefined;
    let current = true;
    void getWorkflowPifRateBand(target.instanceId).then((band) => {
      if (current) setRateFlag(pifRateBandFlag(band));
    });
    return () => {
      current = false;
    };
  }, [isPif, target.instanceId]);

  // Decision 5.10: approverUserId is the Portal user id — under impersonation, the person being
  // viewed as, which is what useUser() holds then. When no pending approver is this user the
  // buttons are off up front; the server's 403 stays as the backstop.
  const pendingApprovers = (group?.approvals ?? []).filter((row) => row.decision === "pending");
  const notYours =
    loadStatus === "loaded" &&
    userId !== null &&
    pendingApprovers.length > 0 &&
    !pendingApprovers.some((row) => row.approverUserId === userId);

  const approverNames = useMemo(
    () => (loadStatus === "loaded" ? (group?.approvals ?? []).map(approverDisplayName) : undefined),
    [group, loadStatus],
  );
  const stamps = useMemo(() => toStamps(group), [group]);
  const progressNote =
    group && group.required > 1 && !group.resolved
      ? `${group.acceptedCount} of ${group.required} required approvals recorded.`
      : undefined;

  const handleReload = async () => {
    const reloaded = await onReloadCase();
    reload();
    setBanner(reloaded ? null : "reload-failed");
  };

  const handleDecision = async (decision: WorkflowApprovalDecision, reason: string) => {
    const outcome = await decide(decision, reason);
    if (!outcome) return;
    if (outcome.kind !== "decided") {
      setBanner(outcome.kind);
      return;
    }
    setBanner(null);
    showToaster(TOASTS[decision], SEVERITIES.SUCCESS);
    // The newest stamp of this decision, from the stage as re-read after it was recorded.
    const recorded = toStamps(outcome.group).filter((stamp) => stamp.decision === decision);
    const reloaded = await onDecided({
      decision,
      reason,
      stageResolved: outcome.stageResolved,
      stamp: recorded.length ? recorded[recorded.length - 1] : null,
    });
    if (!reloaded) setBanner("reload-failed");
  };

  const reloadAction = (
    <Button color="inherit" size="small" onClick={handleReload}>
      Reload
    </Button>
  );

  const bannerNode = (() => {
    if (loadStatus === "error") {
      return (
        <Alert
          severity="error"
          className="workflow-run__alert"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          The approvers for this stage couldn&apos;t be loaded.
        </Alert>
      );
    }
    switch (banner) {
      case "conflict":
        return (
          <Alert severity="warning" className="workflow-run__alert" action={reloadAction}>
            This case was updated by someone else. Reload to see the latest.
          </Alert>
        );
      case "reload-failed":
        return (
          <Alert severity="warning" className="workflow-run__alert" action={reloadAction}>
            Your decision was recorded, but the case couldn&apos;t be reloaded.
          </Alert>
        );
      case "not-permitted":
        return (
          <Alert severity="error" className="workflow-run__alert">
            This signature belongs to the stage&apos;s approver — you can view this stage but not sign it.
          </Alert>
        );
      case "unresolved":
        return (
          <Alert severity="error" className="workflow-run__alert">
            No approver could be resolved for this stage — contact HR.
          </Alert>
        );
      case "not-found":
        return (
          <Alert severity="error" className="workflow-run__alert" action={reloadAction}>
            This case or stage no longer exists.
          </Alert>
        );
      case "failed":
        return (
          <Alert severity="error" className="workflow-run__alert">
            Your decision wasn&apos;t recorded — try again.
          </Alert>
        );
      default:
        return notYours ? (
          <Alert severity="info" className="workflow-run__alert">
            Viewing as {viewingAsLabel(firstName, lastName)}: only{" "}
            {pendingApprovers.map((row) => row.approverName || "the approver").join(" or ")} can decide this step.
          </Alert>
        ) : null;
    }
  })();

  return (
    <WorkflowApprovalStage
      reference={reference}
      definitionName={definitionName}
      stageName={stageName}
      approver=""
      applicant={applicant}
      rateFlag={rateFlag}
      approverNames={approverNames}
      stamps={stamps}
      progressNote={progressNote}
      busy={deciding || loadStatus === "loading"}
      disabled={notYours || banner === "conflict" || banner === "not-permitted" || banner === "unresolved"}
      banner={bannerNode}
      requireCancelReason
      onAccept={(note) => void handleDecision("accept", note)}
      onReject={(reason) => void handleDecision("reject", reason)}
      onCancel={(reason) => void handleDecision("cancel", reason)}
    />
  );
};

export default WorkflowApprovalLive;
